import { mapState } from 'vuex';
import InstaIcon from '../../../public/static/assets/img/svg_icons/instagram_color.svg';
import CheckIcon from '../../../public/static/assets/img/svg_icons/check.svg';
import ConnectionBox from '@components/Common/ConnectionBox';
import CopyLink from '@components/Common/CopyLink';

import { isValueExist } from '@util';

import styled from 'vue-styled-components';
import COLORS from '@design/colors.js';
import {
    applyHoverAndPress,
    FlexColumn,
    FLEX_CENTER,
} from '@styles/common.styled';

import { FB_PAGE_OPTION } from '@/mixins/connection';

export default {
    props: {
        optional: {
            type: Boolean,
            default: false,
        },
        status: String,
        fbInfo: {
            type: Object,
            default() {
                return {};
            },
        },
        token: {
            type: String | Number,
            required: true,
        },
    },
    data() {
        return {
            compFacebookProfile: {},
            isWarning: false,
            addInstaPage: false,
        };
    },
    watch: {
        selectablePages: {
            handler([page] = []) {
                if (
                    isValueExist(page) &&
                    !isValueExist(this.compFacebookProfile)
                ) {
                    this.radioSelected(page);
                }
            },
            immediate: true,
        },
    },
    computed: {
        ...mapState('user', ['permissionsMissing']),
        _compFacebookProfile: {
            get: R.prop('compFacebookProfile'),
            set(value) {
                this.compFacebookProfile = value;
            },
        },
        pages: R.pathOr([], ['fbInfo', 'pages']),
        selectablePages: R.compose(
            R.filter(R.prop('selectable')),
            R.prop('pages')
        ),
        unselectablePages: R.compose(
            R.reject(R.prop('selectable')),
            R.prop('pages')
        ),
        innerStatus() {
            return R.cond([
                [
                    R.pathSatisfies(R.equals(0), ['pages', 'length']),
                    R.always('noPages'),
                ],
                [R.isEmpty, R.always('notAuth')],
                [R.T, R.always('pages')],
            ])(this.fbInfo);
        },
        instaPage: R.path(['_compFacebookProfile', 'instagram']),
        _status() {
            return this.status || this.innerStatus;
        },
        copyUrl() {
            return `${window.location.origin}/#/businessLink?token=${this.token}`;
        },
    },
    methods: {
        radioSelected(page = {}) {
            const oldPage = this._compFacebookProfile || {};

            if (!R.equals(oldPage, page) && page.instagram) {
                this.addInstaPage = true;
            }
            this._compFacebookProfile = page;

            this.$emit('update', {
                ...page,
                facebookPageId: this.idExtractor(page),
                facebookUserToken: R.path(['fbInfo', 'longLifeToken'], this),
                instagram: this.instaPage,
                name: page.name,
            });
        },
        pageDescriptionExtractor: R.compose(
            R.join(' | '),
            R.filter(R.identity),
            R.props(['website', 'id'])
        ),
        idExtractor(data) {
            return data.id || data.facebookPageId;
        },
        handleInstagram() {
            this.addInstaPage = !this.addInstaPage;
        },
        renderSelectablePages() {
            return this.selectablePages.map((page) => {
                return (
                    <ConnectionBox
                        key={page.id || page.facebookPageId}
                        value={page}
                        active={
                            this.idExtractor(this.compFacebookProfile) ===
                            this.idExtractor(page)
                        }
                        nameExtractor={FB_PAGE_OPTION.nameExtractor}
                        descriptionExtractor={this.pageDescriptionExtractor}
                        onClick={() => this.radioSelected(page)}
                    >
                        {this.instaPage && this.instaPage === page.instagram && (
                            <InstaWrapper onClick={this.handleInstagram}>
                                <InstaTitleArea>
                                    <InstaIcon v-view-box />
                                    <InstaTitle>
                                        {this.$t('PROPOSAL.add_instagram_page')}
                                    </InstaTitle>
                                </InstaTitleArea>
                                <InstaPageArea>
                                    <CheckBoxWrapper active={this.addInstaPage}>
                                        {this.addInstaPage && <CheckIcon />}
                                    </CheckBoxWrapper>
                                    <InstaPageName active={this.addInstaPage}>
                                        {page.instagram.username ||
                                            page.instagram.name}
                                    </InstaPageName>
                                </InstaPageArea>
                            </InstaWrapper>
                        )}
                    </ConnectionBox>
                );
            });
        },
        renderUnselectablePages() {
            return this.unselectablePages.map((page) => {
                return (
                    <ConnectionBox
                        key={page.id}
                        value={page}
                        nameExtractor={R.prop('name')}
                        disabled={true}
                        descriptionExtractor={({ reason }) =>
                            reason && this.$t(`PROPOSAL.fb_reason_${reason}`)
                        }
                    />
                );
            });
        },
    },
    render() {
        return (
            <ModalWrapper ref="wrapper">
                <ModalBody>
                    {this._status === 'notAuth' && (
                        <NoAuthContent>
                            <DescSpan>
                                {this.optional && (
                                    <span>({this.$t('COMMON.optional')})</span>
                                )}
                                {this.$t('PROPOSAL.select_fb_page_desc')}
                            </DescSpan>
                            {this.$slots.authBtn}
                        </NoAuthContent>
                    )}

                    {this._status === 'noPages' && (
                        <NoPagesContent>
                            <DescSpan
                                domPropsInnerHTML={this.$t(
                                    'PROPOSAL.no_fb_pages_found'
                                )}
                            />
                        </NoPagesContent>
                    )}

                    {this._status === 'tryAnother' && (
                        <CopyLink
                            label={this.$t('CONNECTION.retry_with_ignito_mode')}
                            link={this.copyUrl}
                        />
                    )}

                    {this._status === 'pages' && (
                        <PagesContents>
                            <DescSpan>
                                {this.$t('PROPOSAL.select_fb_page_ment')}
                            </DescSpan>
                            {this.selectablePages.length > 0 && (
                                <PageElementWrapper>
                                    {this.renderSelectablePages()}
                                    {this.$slots.child}
                                </PageElementWrapper>
                            )}

                            {this.selectablePages.length > 0 &&
                                this.unselectablePages.length > 0 && (
                                    <DisabledDivider />
                                )}

                            <PageElementWrapper class="disabled-area">
                                {this.renderUnselectablePages()}
                            </PageElementWrapper>
                            {this.unselectablePages.length > 0 && (
                                <GuideText
                                    href="https://www.facebook.com/help/510247025775149?helpref=faq_content"
                                    target="_blank"
                                >
                                    {this.$t('PROPOSAL.no_fb_pages_found_link')}
                                </GuideText>
                            )}
                        </PagesContents>
                    )}
                </ModalBody>
            </ModalWrapper>
        );
    },
};

const ModalWrapper = styled.div`
    background-color: white;
    border-radius: 10px;
    max-width: 480px;
`;

const ModalBody = styled.div``;

const NoAuthContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    & > img {
        margin-top: 15px;
        cursor: pointer;
    }
`;

const NoPagesContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    & > img {
        margin-bottom: 15px;
        cursor: pointer;
    }

    span {
        margin-bottom: 0;
    }
`;

const PagesContents = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const PageElementWrapper = styled.div`
    width: 100%;
    margin: -6px 0;
`;

const DescSpan = styled.span`
    font-size: 16px;
    color: #080a0d;
    width: 100%;
    word-break: keep-all;
    word-wrap: break-word;
    margin-bottom: 20px;
`;

const DisabledDivider = styled.div`
    width: 480px;
    height: 1px;
    background-color: #ebeef0;
    margin: 24px 0;
    @media screen and (max-width: 767px) {
        width: 100%;
    }
`;

const GuideText = styled.a`
    font-size: 14px;
    font-weight: 500;
    ${applyHoverAndPress(COLORS['blue-60'])};
    text-decoration: none;
    margin-top: 24px;
    align-self: flex-start;
`;

const InstaWrapper = styled.div`
    ${FlexColumn};
    margin-top: 16px;
    padding-left: 6px;
`;

const InstaTitleArea = styled.div`
    display: flex;
    align-items: center;

    & > svg {
        width: 20px;
        height: 20px;
    }
`;

const InstaTitle = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${COLORS['gray-100']};
    margin-left: 6px;
`;

const InstaPageArea = styled.div`
    display: flex;
    align-items: center;
    margin-top: 20px;
`;

const CheckBoxWrapper = styled('div', { active: Boolean })`
    ${FLEX_CENTER};
    width: 16px;
    height: 16px;
    border-radius: 2px;
    background-color: ${({ active }) =>
        active ? COLORS['blue-60'] : COLORS['gray-15']};

    & > svg {
        fill: white;
        width: 12px;
        height: 12px;
    }
`;

const InstaPageName = styled('span', { active: Boolean })`
    font-size: 14px;
    font-weight: 500;
    margin-left: 10px;
    color: ${({ active }) => (active ? COLORS['blue-60'] : COLORS['gray-100'])};
`;
